import { useEffect, useState } from 'react';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Box, IconButton, Typography } from '@mui/material';

function ProductImageSlider({ images = [], height = 220 }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const slideCount = images.length;

  useEffect(() => {
    if (activeIndex >= slideCount) {
      setActiveIndex(0);
    }
  }, [activeIndex, slideCount]);

  useEffect(() => {
    if (isPaused || slideCount <= 1) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % slideCount);
    }, 3000);

    return () => window.clearInterval(timer);
  }, [isPaused, slideCount]);

  const moveToPrevious = (event) => {
    event.stopPropagation();
    setActiveIndex((current) => (current - 1 + slideCount) % slideCount);
  };

  const moveToNext = (event) => {
    event.stopPropagation();
    setActiveIndex((current) => (current + 1) % slideCount);
  };

  const selectSlide = (index) => (event) => {
    event.stopPropagation();
    setActiveIndex(index);
  };

  if (!slideCount) {
    return (
      <Box
        sx={{
          height,
          borderRadius: 3,
          display: 'grid',
          placeItems: 'center',
          bgcolor: '#f6f0ff',
          border: '1px solid rgba(74,14,143,0.1)',
        }}
      >
        <Typography sx={{ color: 'text.secondary', fontWeight: 700 }}>
          Product image coming soon
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      sx={{
        position: 'relative',
        height,
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: '#fffdfa',
        border: '1px solid rgba(74,14,143,0.08)',
      }}
    >
      {images.map((image, index) => (
        <Box
          key={`${image.filename || image.src}-${index}`}
          component="img"
          src={image.src}
          alt={image.alt || image.name || 'Product image'}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            p: 1.5,
            opacity: index === activeIndex ? 1 : 0,
            transition: 'opacity 450ms ease',
          }}
        />
      ))}

      {slideCount > 1 && (
        <>
          <IconButton
            aria-label="Previous product image"
            onClick={moveToPrevious}
            size="small"
            sx={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(26,5,51,0.72)',
              color: '#ffffff',
              '&:hover': { bgcolor: 'rgba(26,5,51,0.9)' },
            }}
          >
            <ChevronLeftRoundedIcon />
          </IconButton>
          <IconButton
            aria-label="Next product image"
            onClick={moveToNext}
            size="small"
            sx={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(26,5,51,0.72)',
              color: '#ffffff',
              '&:hover': { bgcolor: 'rgba(26,5,51,0.9)' },
            }}
          >
            <ChevronRightRoundedIcon />
          </IconButton>
        </>
      )}

      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 10,
          display: 'flex',
          justifyContent: 'center',
          gap: 0.75,
        }}
      >
        {images.map((image, index) => (
          <Box
            key={`dot-${image.filename || index}`}
            component="button"
            type="button"
            aria-label={`Show product image ${index + 1}`}
            onClick={selectSlide(index)}
            sx={{
              width: index === activeIndex ? 22 : 8,
              height: 8,
              p: 0,
              border: 0,
              borderRadius: 999,
              cursor: 'pointer',
              bgcolor: index === activeIndex ? '#c9a84c' : 'rgba(26,5,51,0.28)',
              transition: 'width 220ms ease, background-color 220ms ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

export default ProductImageSlider;
